import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';

// `d3` (v3) and `nv` (nvd3) attach themselves to the global scope via the
// side-effect `import 'd3'; import 'nvd3';` in core/config/core.library-imports.ts,
// which loads before app code. The original ng2-nvd3 wrapper referenced them as
// bare globals; do the same here to preserve behaviour and avoid d3-v3 typings
// friction on the dynamic chart-configuration calls below.
declare const d3: any;
declare const nv: any;

/**
 * In-repo Ivy replacement for the unmaintained `ng2-nvd3` package.
 *
 * `ng2-nvd3` shipped in the legacy Angular View Engine format and was consumed
 * only because `ngcc` transpiled it at install time; Angular 16 removes `ngcc`,
 * so it could no longer be used (see ANGULAR16_MIGRATION_PLAN.md, blocker P2).
 * This component is a faithful, Ivy-native port of that ~2-file wrapper — same
 * `nvd3` selector and `[options]`/`[data]` inputs — so the ~7 chart consumers
 * keep working unchanged apart from their import path. The nvd3/d3 charting
 * itself is untouched.
 */
@Component({
    selector: 'nvd3',
    template: '',
})
export class NvD3Component implements OnChanges, OnDestroy {
    @Input() options: any;
    @Input() data: any;

    private el: HTMLElement;
    private chart: any;
    private chartType: string | undefined;
    private svg: any;

    constructor(elementRef: ElementRef) {
        this.el = elementRef.nativeElement;
    }

    ngOnChanges(_changes: SimpleChanges): void {
        if (this.options) {
            if (!this.chart || this.chartType !== this.options.chart.type) {
                this.initChart(this.options);
            } else {
                this.updateWithOptions(this.options);
            }
        }
    }

    ngOnDestroy(): void {
        this.clearElement();
    }

    private initChart(options: any): void {
        this.clearElement();
        if (!options) {
            return;
        }
        this.chart = nv.models[options.chart.type]();
        this.chartType = this.options.chart.type;
        this.chart.id = Math.random().toString(36).substr(2, 15);
        this.updateWithOptions(options);

        nv.addGraph(() => {
            if (!this.chart) {
                return;
            }
            if (this.chart.resizeHandler) {
                this.chart.resizeHandler.clear();
            }
            this.chart.resizeHandler = nv.utils.windowResize(() => {
                this.chart && this.chart.update && this.chart.update();
            });
            return this.chart;
        }, options.chart['callback']);
    }

    private updateWithOptions(options: any): void {
        if (!options) {
            return;
        }
        for (const key in this.chart) {
            if (!this.chart.hasOwnProperty(key)) {
                continue;
            }
            if (key[0] === '_') {
                // private nvd3 member — skip
            } else if (
                [
                    'clearHighlights',
                    'highlightPoint',
                    'id',
                    'options',
                    'resizeHandler',
                    'state',
                    'open',
                    'close',
                    'tooltipContent',
                ].indexOf(key) >= 0
            ) {
                // non-configurable member — skip
            } else if (key === 'dispatch') {
                this.configureEvents(this.chart[key], options.chart[key]);
            } else if (
                [
                    'bars',
                    'bars1',
                    'bars2',
                    'boxplot',
                    'bullet',
                    'controls',
                    'discretebar',
                    'distX',
                    'distY',
                    'interactiveLayer',
                    'legend',
                    'lines',
                    'lines1',
                    'lines2',
                    'multibar',
                    'pie',
                    'scatter',
                    'scatters1',
                    'scatters2',
                    'sparkline',
                    'stack1',
                    'stack2',
                    'sunburst',
                    'tooltip',
                    'x2Axis',
                    'xAxis',
                    'y1Axis',
                    'y2Axis',
                    'y3Axis',
                    'y4Axis',
                    'yAxis',
                    'yAxis1',
                    'yAxis2',
                    'sankeyChart',
                ].indexOf(key) >= 0 ||
                (key === 'stacked' && options.chart.type === 'stackedAreaChart')
            ) {
                this.configure(this.chart[key], options.chart[key], options.chart.type);
            } else if (
                (key === 'xTickFormat' || key === 'yTickFormat') &&
                options.chart.type === 'lineWithFocusChart'
            ) {
                // handled by the focus chart — skip
            } else if (key === 'tooltips' && options.chart.type === 'boxPlotChart') {
                // skip
            } else if (
                (key === 'tooltipXContent' || key === 'tooltipYContent') &&
                options.chart.type === 'scatterChart'
            ) {
                // skip
            } else if (options.chart[key] === undefined || options.chart[key] === null) {
                // nothing to apply
            } else {
                this.chart[key](options.chart[key]);
            }
        }
        this.updateWithData(this.data);
    }

    private updateWithData(data: any): void {
        if (data) {
            const svgElement = this.el.querySelector('svg');
            if (!svgElement) {
                this.svg = d3.select(this.el).append('svg');
            } else {
                this.svg = d3.select(svgElement);
            }
            this.updateSize();
            this.svg.datum(data).call(this.chart);
        }
    }

    private updateSize(): void {
        if (this.svg) {
            let h: any;
            let w: any;
            if ((h = this.options.chart.height)) {
                if (!isNaN(+h)) {
                    h += 'px';
                }
                this.svg.attr('height', h).style({ height: h });
            }
            if ((w = this.options.chart.width)) {
                if (!isNaN(+w)) {
                    w += 'px';
                }
                this.svg.attr('width', w).style({ width: w });
            } else {
                this.svg.attr('width', '100%').style({ width: '100%' });
            }
        }
    }

    private configure(chart: any, options: any, chartType: string): void {
        if (chart && options) {
            for (const key in chart) {
                if (!chart.hasOwnProperty(key)) {
                    continue;
                }
                if (key[0] === '_') {
                    // skip
                } else if (key === 'dispatch') {
                    this.configureEvents(chart[key], options[key]);
                } else if (key === 'tooltip') {
                    this.configure(chart[key], options[key], chartType);
                } else if (key === 'contentGenerator') {
                    if (options[key]) {
                        chart[key](options[key]);
                    }
                } else if (
                    [
                        'axis',
                        'clearHighlights',
                        'defined',
                        'highlightPoint',
                        'nvPointerEventsClass',
                        'options',
                        'rangeBand',
                        'rangeBands',
                        'scatter',
                        'open',
                        'close',
                    ].indexOf(key) === -1
                ) {
                    if (options[key] === undefined || options[key] === null) {
                        // nothing to apply
                    } else {
                        chart[key](options[key]);
                    }
                }
            }
        }
    }

    private configureEvents(dispatch: any, options: any): void {
        if (dispatch && options) {
            for (const key in dispatch) {
                if (!dispatch.hasOwnProperty(key)) {
                    continue;
                }
                if (options[key] === undefined || options[key] === null) {
                    // nothing to bind
                } else {
                    dispatch.on(key + '._', options[key]);
                }
            }
        }
    }

    private clearElement(): void {
        this.el.innerHTML = '';
        if (this.chart && this.chart.tooltip && this.chart.tooltip.id) {
            d3.select('#' + this.chart.tooltip.id()).remove();
        }
        if (nv['graphs'] && this.chart) {
            for (let i = nv['graphs'].length - 1; i >= 0; i--) {
                if (nv['graphs'][i] && nv['graphs'][i].id === this.chart.id) {
                    nv['graphs'].splice(i, 1);
                }
            }
        }
        if (nv.tooltip && nv.tooltip.cleanup) {
            nv.tooltip.cleanup();
        }
        if (this.chart && this.chart.resizeHandler) {
            this.chart.resizeHandler.clear();
        }
        this.chart = null;
    }
}
